import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  TextField,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Fade,
  ClickAwayListener,
  Popper,
} from "@mui/material";
import {
  Close,
  LocationOn,
  Business,
  Home,
  Place,
  Search,
} from "@mui/icons-material";
import "./FrenchAddressAutocomplete.scss";

// ============================================================================
// TYPES
// ============================================================================

export interface AddressResult {
  fulltext: string;
  street?: string;
  zipcode?: string;
  city?: string;
  country?: string;
  type?: string;
  kind?: string;
  x?: number;
  y?: number;
  geometry?: {
    type: string;
    coordinates: [number, number];
  };
  coordinates?: {
    lon: number;
    lat: number;
  };
}

interface FrenchAddressAutocompleteProps {
  value: string;
  onChange: (address: AddressResult | null) => void;
  onTextChange?: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string | false;
  fullWidth?: boolean;
  territory?: string;
  type?: string;
  fetchCoordinates?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const API_BASE_URL = "https://data.geopf.fr/geocodage";
const DEBOUNCE_DELAY = 300;
const MIN_CHARS = 3;
const MAX_RESPONSES = 10;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAddressIcon = (result: AddressResult | null): JSX.Element => {
  if (!result) return <LocationOn />;

  const type = result.country || result.type;
  const kind = result.kind || "";

  if (type === "StreetAddress") {
    if (kind.includes("house") || kind.includes("maison")) {
      return <Home sx={{ color: "#4caf50" }} />;
    }
    return <LocationOn sx={{ color: "#2196f3" }} />;
  }

  if (type === "PositionOfInterest") {
    return <Business sx={{ color: "#ff9800" }} />;
  }

  return <Place sx={{ color: "#9c27b0" }} />;
};

const formatAddress = (
  result: AddressResult | null,
): { primary: string; secondary: string } => {
  if (!result) return { primary: "", secondary: "" };

  const primary = result.fulltext || result.street || "";
  const parts: string[] = [];

  if (result.zipcode) parts.push(result.zipcode);
  if (result.city) parts.push(result.city);

  const secondary = parts.join(" ");

  return { primary, secondary };
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useAddressSearch = ({
  territory,
  type,
}: {
  territory?: string;
  type?: string;
}) => {
  const [results, setResults] = useState<AddressResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const search = useCallback(
    async (text: string) => {
      const currentRequestId = ++requestIdRef.current;

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (!text || text.trim().length < MIN_CHARS) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        setResults([]);
        setIsLoading(false);
        setError(null);
        setSearchQuery("");
        return;
      }

      setSearchQuery(text);
      setError(null);

      debounceTimeoutRef.current = setTimeout(async () => {
        setIsLoading(true);

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        try {
          const params = new URLSearchParams({
            text: text.trim(),
            maximumResponses: MAX_RESPONSES.toString(),
          });

          if (territory) {
            params.append("terr", territory);
          }

          if (type) {
            params.append("type", type);
          }

          const response = await fetch(
            `${API_BASE_URL}/completion?${params.toString()}`,
            {
              signal: abortControllerRef.current.signal,
              headers: {
                Accept: "application/json",
              },
            },
          );

          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          if (response.status === 429) {
            setError("Limite de requêtes atteinte. Veuillez patienter...");
            setIsLoading(false);
            return;
          }

          if (!response.ok) {
            throw new Error(`Erreur HTTP: ${response.status}`);
          }

          const data = await response.json();

          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          if (data.results && Array.isArray(data.results)) {
            setResults(data.results);
          } else {
            setResults([]);
          }
          setError(null);
        } catch (err: any) {
          if (err.name === "AbortError") {
            return;
          }

          if (currentRequestId !== requestIdRef.current) {
            return;
          }

          console.error("Address search error:", err);
          setError("Erreur lors de la recherche. Veuillez réessayer.");
          setResults([]);
        } finally {
          if (currentRequestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      }, DEBOUNCE_DELAY);
    },
    [territory, type],
  );

  const clearSearch = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setResults([]);
    setIsLoading(false);
    setError(null);
    setSearchQuery("");
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    results,
    isLoading,
    error,
    searchQuery,
    search,
    clearSearch,
  };
};

const useGeocode = () => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const geocode = useCallback(
    async (address: AddressResult): Promise<AddressResult> => {
      if (!address || !address.fulltext) {
        return address;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsGeocoding(true);

      try {
        const response = await fetch(
          `${API_BASE_URL}/search?q=${encodeURIComponent(
            address.fulltext,
          )}&limit=1`,
          {
            signal: abortControllerRef.current.signal,
          },
        );

        if (!response.ok) {
          return address;
        }

        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const feature = data.features[0];
          return {
            ...address,
            geometry: feature.geometry,
            coordinates: feature.geometry?.coordinates
              ? {
                  lon: feature.geometry.coordinates[0],
                  lat: feature.geometry.coordinates[1],
                }
              : undefined,
          };
        }

        return address;
      } catch (err: any) {
        if (err.name === "AbortError") {
          return address;
        }
        console.error("Geocoding error:", err);
        return address;
      } finally {
        setIsGeocoding(false);
      }
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return { geocode, isGeocoding };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FrenchAddressAutocomplete: React.FC<FrenchAddressAutocompleteProps> = ({
  value,
  onChange,
  onTextChange,
  label = "Adresse",
  placeholder = "Tapez l'adresse complète",
  disabled = false,
  required = false,
  error: externalError = false,
  helperText,
  fullWidth = true,
  territory,
  type = "StreetAddress,PositionOfInterest",
  fetchCoordinates = true,
}) => {
  // State
  const [inputValue, setInputValue] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasSelectedValue, setHasSelectedValue] = useState(false);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Hooks
  const { geocode, isGeocoding } = useGeocode();
  const {
    results,
    isLoading,
    error: searchError,
    searchQuery,
    search,
    clearSearch,
  } = useAddressSearch({
    territory,
    type,
  });

  // Derived state
  const showDropdown = useMemo(() => {
    return (
      isOpen &&
      inputValue.length >= MIN_CHARS &&
      !hasSelectedValue &&
      (results.length > 0 || isLoading || searchError)
    );
  }, [
    isOpen,
    inputValue,
    hasSelectedValue,
    results.length,
    isLoading,
    searchError,
  ]);

  const isProcessing = isLoading || isGeocoding;

  // Sync value prop with input
  useEffect(() => {
    setInputValue(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Event handlers
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setHasSelectedValue(false);
      setHighlightedIndex(-1);

      if (onTextChange) onTextChange(newValue);

      search(newValue);

      if (newValue.length >= MIN_CHARS) {
        setIsOpen(true);
      }
    },
    [search, onTextChange],
  );

  const handleSelect = useCallback(
    async (result: AddressResult) => {
      if (!result) return;

      setInputValue(result.fulltext || "");
      setHasSelectedValue(true);
      setIsOpen(false);
      setHighlightedIndex(-1);
      clearSearch();

      let enrichedResult = result;
      if (fetchCoordinates && !result.geometry && !result.coordinates) {
        enrichedResult = await geocode(result);
      }

      onChange(enrichedResult);
    },
    [onChange, clearSearch, fetchCoordinates, geocode],
  );

  const handleClear = useCallback(() => {
    setInputValue("");
    setHasSelectedValue(false);
    setIsOpen(false);
    setHighlightedIndex(-1);
    clearSearch();
    onChange(null);

    if (inputRef.current) inputRef.current.focus();
  }, [clearSearch, onChange]);

  const handleFocus = useCallback(() => {
    if (!hasSelectedValue && inputValue.length >= MIN_CHARS) {
      setIsOpen(true);
    }
  }, [hasSelectedValue, inputValue]);

  const handleClickAway = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown) return;

      const itemCount = results.length;
      if (itemCount === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev < itemCount - 1 ? prev + 1 : 0));
          break;

        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : itemCount - 1));
          break;

        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0 && results[highlightedIndex]) {
            handleSelect(results[highlightedIndex]);
          }
          break;

        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        case "Tab":
          if (highlightedIndex >= 0 && results[highlightedIndex]) {
            handleSelect(results[highlightedIndex]);
          }
          setIsOpen(false);
          break;

        default:
          break;
      }
    },
    [showDropdown, results, highlightedIndex, handleSelect],
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const listItems = listRef.current.querySelectorAll('[role="option"]');
      if (listItems[highlightedIndex]) {
        listItems[highlightedIndex].scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [highlightedIndex]);

  // Render helpers
  const renderEndAdornment = () => {
    return (
      <InputAdornment position="end">
        {isProcessing && <CircularProgress size={20} sx={{ marginRight: 1 }} />}
        {inputValue && !isProcessing && (
          <IconButton
            size="small"
            onClick={handleClear}
            title="Effacer"
            disabled={disabled}
            sx={{ padding: "4px" }}
          >
            <Close sx={{ fontSize: "1rem" }} />
          </IconButton>
        )}
      </InputAdornment>
    );
  };

  const renderDropdownContent = () => {
    if (searchError) {
      return (
        <Box className="french-address-autocomplete__status french-address-autocomplete__status--error">
          <Typography variant="body2" color="error">
            {searchError}
          </Typography>
        </Box>
      );
    }

    if (isLoading) {
      return (
        <Box className="french-address-autocomplete__status french-address-autocomplete__status--loading">
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
            Recherche en cours...
          </Typography>
        </Box>
      );
    }

    if (results.length === 0 && searchQuery.length >= MIN_CHARS) {
      return (
        <Box className="french-address-autocomplete__no-results">
          <Search sx={{ fontSize: 48, color: "#ccc", marginBottom: 1 }} />
          <Typography variant="body2" color="textSecondary">
            Aucune adresse trouvée pour &ldquo;{searchQuery}&rdquo;
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Essayez d&apos;ajouter plus de détails (numéro, ville, code postal)
          </Typography>
        </Box>
      );
    }

    return (
      <List ref={listRef} dense disablePadding>
        {results.map((result, index) => {
          const { primary, secondary } = formatAddress(result);
          const isHighlighted = index === highlightedIndex;

          return (
            <ListItem
              key={`${result.fulltext}-${index}`}
              button
              selected={isHighlighted}
              onClick={() => handleSelect(result)}
              onMouseEnter={() => setHighlightedIndex(index)}
              role="option"
              aria-selected={isHighlighted}
              className="french-address-autocomplete__list-item"
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {getAddressIcon(result)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    className="french-address-autocomplete__address-text"
                  >
                    {primary}
                  </Typography>
                }
                secondary={
                  secondary && (
                    <Typography
                      variant="caption"
                      className="french-address-autocomplete__address-subtext"
                    >
                      {secondary}
                    </Typography>
                  )
                }
                disableTypography
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        ref={containerRef}
        className="french-address-autocomplete"
        sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}
      >
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          error={externalError}
          helperText={helperText}
          fullWidth={fullWidth}
          variant="outlined"
          autoComplete="off"
          inputProps={{
            "aria-autocomplete": "list",
            "aria-controls": showDropdown
              ? "french-address-autocomplete-listbox"
              : undefined,
            "aria-activedescendant":
              highlightedIndex >= 0
                ? `address-option-${highlightedIndex}`
                : undefined,
          }}
          InputProps={{
            endAdornment: renderEndAdornment(),
          }}
        />

        <Popper
          open={Boolean(showDropdown)}
          anchorEl={containerRef.current}
          placement="bottom-start"
          sx={{
            zIndex: 1300,
            width: containerRef.current?.offsetWidth || "100%",
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={200}>
              <Paper
                id="french-address-autocomplete-listbox"
                role="listbox"
                elevation={8}
                className="french-address-autocomplete__dropdown"
              >
                {renderDropdownContent()}
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default FrenchAddressAutocomplete;
